import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import { Button, Modal } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { isAdminRoleSelector } from '@store/common/selectors';
import { selectedKPIConfigSelector } from '@store/overviewDashboard/base/selectors';
import {
  thresholdConfigsSelector,
  selectedThresholdConfigSelector,
  isEditableFlagSelector,
  createNewClickedSelector,
  loadingSelector,
} from '@store/overviewDashboard/spiThresholds/selectors';
import { SYSTEM_DEFINED_SPI_IDS, DEFAULT_KPI_ID } from '@src/utils/kpi';
import {
  setSelectedKPIConfig,
  fetchSPIThresholds,
} from '@store/overviewDashboard/spiThresholds/actions';
import { Retry } from '@components/common';
import { setSelectedKPIId } from '@src/store/overviewDashboard/base/actions';
import { ErrorMessage, SPIStyle } from './styled';
import {
  onClickOfCreateNew,
  onClickOfSave,
  validateKpiNameInUtil,
  closePopupAndResetState,
} from './helper';
import Content from './content';
import Header from './header';

const SPIPopup = ({ isSPIPopupOpen, cancelEdit, currentShareId, pageType }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isAdmin = useSelector(isAdminRoleSelector);
  const selectedKPIConfigFromBase = useSelector(selectedKPIConfigSelector);
  const selectedKPIConfigID = selectedKPIConfigFromBase.kpiID;
  const config = useSelector(thresholdConfigsSelector);
  const selectedKPIConfig = useSelector(selectedThresholdConfigSelector);
  const isBeingEdited = useSelector(isEditableFlagSelector);
  const isEditable =
    (selectedKPIConfig &&
      !SYSTEM_DEFINED_SPI_IDS.includes(selectedKPIConfig.kpiID) &&
      isAdmin &&
      !pageType) ||
    isBeingEdited;
  const isCreateNewClicked = useSelector(createNewClickedSelector);
  const [isKpiNameValid, setValidKpiNameValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const closePopup = () => {
    closePopupAndResetState(
      dispatch,
      selectedKPIConfigFromBase,
      setValidKpiNameValid,
      setErrorMessage,
      cancelEdit,
    );
  };

  const validateKpiName = () =>
    validateKpiNameInUtil(
      selectedKPIConfig,
      config,
      isBeingEdited,
      isCreateNewClicked,
      setValidKpiNameValid,
      setErrorMessage,
      isKpiNameValid,
    );

  useEffect(() => {
    const isKpiAvailable = config.find((item) => item.kpiID === selectedKPIConfigFromBase.kpiID);
    const defaultKPI = config.find((item) => item.kpiID === DEFAULT_KPI_ID);
    if (pageType && !isKpiAvailable) {
      dispatch(setSelectedKPIId(defaultKPI));
      dispatch(setSelectedKPIConfig(defaultKPI));
    }
  }, [currentShareId]);

  useEffect(() => {
    if (selectedKPIConfig) {
      validateKpiName();
    }
  }, [selectedKPIConfig, isBeingEdited, isCreateNewClicked]);

  const isConfigChanged = () => {
    const configFromList = config.find((item) => item.kpiID === selectedKPIConfig.kpiID);
    return !isEqual(selectedKPIConfig, configFromList);
  };

  const onSave = () => {
    queryClient.invalidateQueries(['selected-kpi']);
    onClickOfSave(
      pageType,
      dispatch,
      cancelEdit,
      selectedKPIConfig,
      validateKpiName,
      isEditable,
      isConfigChanged,
    );
  };

  const onCreateNew = () => {
    queryClient.invalidateQueries(['selected-kpi']);
    queryClient.invalidateQueries(['kpi-list']);
    onClickOfCreateNew(dispatch, cancelEdit, selectedKPIConfig, validateKpiName);
  };

  const getFooterButtons = () => {
    const footerButtons = [
      <Button key="discard" onClick={closePopup}>
        Cancel
      </Button>,
      <Button
        key="save"
        type="primary"
        disabled={(selectedKPIConfig && isEmpty(selectedKPIConfig)) || !isKpiNameValid}
        onClick={isCreateNewClicked ? onCreateNew : onSave}
      >
        {isCreateNewClicked || isConfigChanged() ? 'Save And Apply' : 'Apply'}
      </Button>,
    ];
    return footerButtons;
  };

  const { loading, errorMsg } = useSelector(loadingSelector);
  const isError = !!(errorMsg && errorMsg.length > 0);

  const handleRetryRequest = () => {
    dispatch(fetchSPIThresholds());
  };

  return (
    <Modal
      width="500px"
      title="Edit KPI"
      open={isSPIPopupOpen}
      onCancel={closePopup}
      footer={getFooterButtons()}
    >
      <Retry
        isLoading={loading || !selectedKPIConfig}
        showError={isError}
        fetchData={handleRetryRequest}
      >
        <React.Fragment key="spi-popup">
          <Header
            isAdmin={isAdmin}
            config={config}
            selectedKPIConfig={selectedKPIConfig}
            isEditable={isEditable}
            selectedKPIConfigID={selectedKPIConfigID}
            closeEditPopup={cancelEdit}
            pageType={pageType}
            isSPIPopupOpen={isSPIPopupOpen}
          />
          <ErrorMessage>{isKpiNameValid ? '' : errorMessage}</ErrorMessage>
          <SPIStyle>A stream is considered good if it has no technical errors and:</SPIStyle>
          <Content
            key={selectedKPIConfig && selectedKPIConfig.kpiID}
            selectedKPIConfig={selectedKPIConfig}
            isEditable={isEditable}
          />
        </React.Fragment>
      </Retry>
    </Modal>
  );
};

export default SPIPopup;
